import { z } from 'zod';
import { Uuid, IsoDateTime, success } from '@secretaa/http-contract';
import { CreateRecordBody, UpdateRecordBody } from './dynamic-data.ts';
import { Ref } from './work-os/primitives.ts';

const frame = { idempotencyKey: Uuid, manifestHash: z.string().regex(/^[a-f0-9]{64}$/) };
/** Explicit user writes only. A receipt is not an automation grant or a UI publication. */
export const RecordCommandBody = z.discriminatedUnion('operation', [
  z.object({ ...frame, operation: z.literal('CREATE'), input: CreateRecordBody }).strict(),
  z.object({
    ...frame, operation: z.literal('UPDATE'), recordId: Uuid,
    expectedVersion: z.number().int().min(1).max(2147483646), input: UpdateRecordBody,
  }).strict(),
]);
export const RecordCommandReceipt = z.object({
  idempotencyKey: Uuid,
  scope: z.object({ workspaceId: Uuid, projectId: Uuid }).strict(),
  subjectId: Uuid, collectionId: Uuid, operation: z.enum(['CREATE', 'UPDATE']),
  recordRef: Ref, recordScopeId: Uuid.nullable(),
  commandDigest: z.string().regex(/^[a-f0-9]{64}$/), committedAt: IsoDateTime,
}).strict();
export const RecordCommandResponse = success(RecordCommandReceipt);
export type RecordCommandReceipt = z.infer<typeof RecordCommandReceipt>;

export function parseRecordCommand(input: unknown) {
  const body = RecordCommandBody.parse(input);
  const values = body.input.values;
  const normalized = values
    ? Object.fromEntries(Object.entries(values).map(([id, value]) => [id.toLowerCase(), value]))
    : undefined;
  if (values && normalized && Object.keys(normalized).length !== Object.keys(values).length) {
    throw new Error('AMBIGUOUS_RECORD_FIELD');
  }
  return {
    ...body, idempotencyKey: body.idempotencyKey.toLowerCase(),
    ...(body.operation === 'UPDATE' ? { recordId: body.recordId.toLowerCase() } : {}),
    input: {
      ...body.input,
      ...(body.input.scopeId !== undefined ? { scopeId: body.input.scopeId?.toLowerCase() ?? null } : {}),
      ...(normalized ? { values: normalized } : {}),
    },
  } as z.infer<typeof RecordCommandBody>;
}

export const SettleRecordCommandBody = z.object({
  idempotencyKey: Uuid, commandDigest: z.string().regex(/^[a-f0-9]{64}$/), operation: z.enum(['CREATE','UPDATE']),
}).strict();
export const RecordCommandClosure = SettleRecordCommandBody.extend({
  scope: z.object({ workspaceId:Uuid, projectId:Uuid }).strict(), subjectId:Uuid, collectionId:Uuid, closedAt:IsoDateTime,
}).strict();
export type RecordCommandClosure = z.infer<typeof RecordCommandClosure>;
export const RecordCommandSettlement = z.discriminatedUnion('status',[
  z.object({status:z.literal('COMMITTED'),receipt:RecordCommandReceipt}).strict(),
  z.object({status:z.literal('CLOSED'),closure:RecordCommandClosure}).strict(),
]);
export const RecordCommandSettlementResponse = success(RecordCommandSettlement);
